// src/settings/UserPage.jsx
// -----------------------------------------------------------------------------
// UserPage (cadastro/edição de usuário)
// - Mantém toda a lógica original (fetch, validações, permissões, salvar, etc.)
// - Ajustes visuais apenas (dark/light), tamanhos compactos e espaçamentos
// - Accordions com cabeçalho destacado pelo tema, ícones com contraste correto
// - Layout responsivo: linhas viram coluna no mobile
// -----------------------------------------------------------------------------

import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormGroup,
  TextField,
  Button,
  InputAdornment,
  OutlinedInput,
  Dialog,
  DialogContent,
  DialogActions,
  useTheme,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import CachedIcon from "@mui/icons-material/Cached";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import EditItemView from "./components/EditItemView";
import EditAttributesAccordion from "./components/EditAttributesAccordion";
import { useTranslation } from "../common/components/LocalizationProvider";
import useUserAttributes from "../common/attributes/useUserAttributes";
import { sessionActions } from "../store";
import SelectField from "../common/components/SelectField";
import SettingsMenu from "./components/SettingsMenu";
import useCommonUserAttributes from "../common/attributes/useCommonUserAttributes";
import {
  useAdministrator,
  useRestriction,
  useManager,
} from "../common/util/permissions";
import { useCatch } from "../reactHelper";
import useMapStyles from "../map/core/useMapStyles";
import { map } from "../map/core/MapView";
import useSettingsStyles from "./common/useSettingsStyles";
import { useLocalization } from "../common/components/LocalizationProvider";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SecurityIcon from "@mui/icons-material/Security";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import openDatePicker from "../common/util/openDatePicker";
import MapIcon from "@mui/icons-material/Map";
import PersonIcon from "@mui/icons-material/Person";
import fetchOrThrow from "../common/util/fetchOrThrow";

const UserPage = () => {
  const { classes } = useSettingsStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const t = useTranslation();
  const theme = useTheme();
  const { htmlLang } = useLocalization();

  const admin = useAdministrator();
  const manager = useManager();
  const fixedEmail = useRestriction("fixedEmail");

  const currentUser = useSelector((state) => state.session.user);
  const registrationEnabled = useSelector(
    (state) => state.session.server.registration
  );
  const openIdForced = useSelector((state) => state.session.server.openIdForce);
  const totpEnable = useSelector(
    (state) => state.session.server.attributes.totpEnable
  );
  const totpForce = useSelector(
    (state) => state.session.server.attributes.totpForce
  );

  const mapStyles = useMapStyles();
  const commonUserAttributes = useCommonUserAttributes(t);
  const userAttributes = useUserAttributes(t);

  const { id } = useParams();

  // -----------------------------------------------------------------------------
  // Helpers para garantir a existência do "attributes.form" (evita erro em inputs)
  // -----------------------------------------------------------------------------
  const ensureFormStructure = (currentItem) => {
    if (!currentItem) return currentItem;
    return {
      ...currentItem,
      attributes: {
        ...currentItem.attributes,
        form: currentItem.attributes?.form || {},
      },
    };
  };

  const [item, setItem] = useState(() =>
    ensureFormStructure(id === currentUser.id.toString() ? currentUser : null)
  );
  const handleSetItem = (newItem) => {
    setItem(ensureFormStructure(newItem));
  };

  // Atualiza um campo dentro de attributes.form de maneira segura
  const updateFormField = (key, value) => {
    setItem((prev) =>
      ensureFormStructure({
        ...prev,
        attributes: {
          ...prev.attributes,
          form: {
            ...prev.attributes?.form,
            [key]: value,
          },
        },
      })
    );
  };

  const updateAttributeValue = (key, value) => {
    setItem((prev) =>
      ensureFormStructure({
        ...prev,
        attributes: {
          ...(prev?.attributes || {}),
          [key]: value,
        },
      })
    );
  };

  const [deleteEmail, setDeleteEmail] = useState();
  const [deleteFailed, setDeleteFailed] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [revokeToken, setRevokeToken] = useState("");
  const expirationRef = useRef(null);

  // Filtra atributos aninhados (mantém objetos não-renderizáveis fora do editor plano)
  const filterAttributes = (attributes) => {
    if (!attributes) return {};
    const filteredAttributes = {};
    Object.keys(attributes).forEach((key) => {
      const value = attributes[key];
      if (
        value === null ||
        value === undefined ||
        typeof value !== "object" ||
        Array.isArray(value)
      ) {
        filteredAttributes[key] = value;
      }
    });
    return filteredAttributes;
  };

  // Preserva attributes.form ao atualizar outros atributos
  const updateAttributes = (newAttributes) => {
    const updatedAttributes = { ...newAttributes };
    if (item.attributes) {
      if (item.attributes.form) updatedAttributes.form = item.attributes.form;
    }
    setItem({ ...item, attributes: updatedAttributes });
  };

  // Exclusão da própria conta (mantido)
  const handleDelete = useCatch(async () => {
    if (deleteEmail === currentUser.email) {
      setDeleteFailed(false);
      await fetchOrThrow(`/api/users/${currentUser.id}`, { method: "DELETE" });
      navigate("/login");
      dispatch(sessionActions.updateUser(null));
    } else {
      setDeleteFailed(true);
    }
  });

  const handleGenerateTotp = useCatch(async () => {
    const response = await fetchOrThrow("/api/users/totp", {
      method: "POST",
    });
    const totpKey = await response.text();
    setItem((prev) => (prev ? { ...prev, totpKey } : prev));
  });

  const closeRevokeDialog = () => {
    setRevokeDialogOpen(false);
    setRevokeToken("");
  };

  const handleRevokeToken = useCatch(async () => {
    await fetchOrThrow("/api/session/token/revoke", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ token: revokeToken }).toString(),
    });
    closeRevokeDialog();
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const attribute = searchParams.get("attribute");

  // Ao abrir com ?attribute=foo garante a criação do atributo visualizado
  useEffect(() => {
    if (
      item &&
      attribute &&
      item.attributes &&
      !item.attributes.hasOwnProperty(attribute)
    ) {
      const updatedAttributes = { ...item.attributes, [attribute]: "" };
      setItem(ensureFormStructure({ ...item, attributes: updatedAttributes }));

      const newParams = new URLSearchParams(searchParams);
      newParams.delete("attribute");
      setSearchParams(newParams, { replace: true });
    }
  }, [item, searchParams, setSearchParams, attribute]);

  // Ao salvar o próprio usuário, atualiza o estado global
  const onItemSaved = (result) => {
    if (result.id === currentUser.id) {
      dispatch(sessionActions.updateUser(result));
    }
  };

  // Validação original mantida
  const validate = () =>
    item &&
    item.name &&
    item.email &&
    (item.id || item.password) &&
    (admin || !totpForce || item.totpKey);

  // -----------------------------------------------------------------------------
  // Estilos/SX reutilizáveis (apenas UI)
  // -----------------------------------------------------------------------------
  const summarySx = {
    // Cabeçalho do accordion destacado: leve no light, sutil no dark
    bgcolor:
      theme.palette.mode === "dark"
        ? theme.palette.background.default
        : theme.palette.primary.light,
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
    "& .MuiAccordionSummary-content": {
      alignItems: "center",
      gap: 1,
      my: 0.5,
    },
  };

  const detailsSx = {
    // Corpo com espaçamento consistente e layout flex-column
    display: "flex",
    flexDirection: "column",
    gap: 2,
    p: { xs: 2, sm: 2.5 },
    bgcolor: theme.palette.background.paper,
  };

  const rowSx = {
    display: "flex",
    width: "100%",
    gap: 2,
    flexDirection: { xs: "column", sm: "row" }, // coluna no mobile
  };

  const fieldSx = { minWidth: 0, flex: 1 }; // evita quebra e mantém colunas fluídas
  const small = { size: "small" }; // tamanho compacto consistente

  // -----------------------------------------------------------------------------
  // Renderização
  // -----------------------------------------------------------------------------
  return (
    <EditItemView
      endpoint="users"
      item={item}
      setItem={handleSetItem}
      defaultItem={admin ? { deviceLimit: -1 } : {}}
      validate={validate}
      onItemSaved={onItemSaved}
      menu={<SettingsMenu />}
      breadcrumbs={["settingsTitle", "settingsUser"]}
    >
      {item && (
        <>
          {/* -------------------------------------------------------------------
              OBRIGATÓRIOS
          ------------------------------------------------------------------- */}
          <Accordion defaultExpanded={!attribute} disableGutters sx={{ mb: 2, borderRadius: 2, overflow: "hidden", border: `1px solid ${theme.palette.divider}` }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
              <Box display="flex" alignItems="center" gap={1.25}>
                {/* Ícone usa cor do tema para bom contraste */}
                <NewReleasesIcon sx={{ color: theme.palette.primary.main }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {t("sharedRequired")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details} sx={detailsSx}>
              <Box sx={rowSx}>
                {/* Nome */}
                <Box sx={{ ...fieldSx, flex: 2 }}>
                  <TextField
                    {...small}
                    fullWidth
                    value={item.name || ""}
                    onChange={(e) => setItem({ ...item, name: e.target.value })}
                    label={t("sharedName")}
                    required
                  />
                </Box>

                {/* E-mail */}
                <Box sx={{ ...fieldSx, flex: 2 }}>
                  <TextField
                    {...small}
                    fullWidth
                    value={item.email || ""}
                    onChange={(e) =>
                      setItem({ ...item, email: e.target.value })
                    }
                    label={t("userEmail")}
                    disabled={fixedEmail && item.id === currentUser.id}
                    required
                  />
                </Box>

                {/* Senha (apenas quando necessário) */}
                {!openIdForced && (
                  <Box sx={{ ...fieldSx, flex: 1 }}>
                    <TextField
                      {...small}
                      fullWidth
                      type="password"
                      onChange={(e) =>
                        setItem({ ...item, password: e.target.value })
                      }
                      label={t("userPassword")}
                      required={!item.id}
                    />
                  </Box>
                )}
                {totpEnable && (
                  <Box sx={{ ...fieldSx, flex: 1 }}>
                    <FormControl fullWidth {...small}>
                      <InputLabel>{t("loginTotpKey")}</InputLabel>
                      <OutlinedInput
                        readOnly
                        label={t("loginTotpKey")}
                        value={item.totpKey || ""}
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              edge="end"
                              onClick={handleGenerateTotp}
                            >
                              <CachedIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              edge="end"
                              onClick={() =>
                                setItem((prev) =>
                                  prev ? { ...prev, totpKey: null } : prev
                                )
                              }
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        }
                      />
                    </FormControl>
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* -------------------------------------------------------------------
              PREFERÊNCIAS
          ------------------------------------------------------------------- */}
          <Accordion disableGutters sx={{ mb: 2, borderRadius: 2, overflow: "hidden", border: `1px solid ${theme.palette.divider}` }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
              <Box display="flex" alignItems="center" gap={1.25}>
                <PersonIcon sx={{ color: theme.palette.primary.main }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {t("sharedPreferences")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details} sx={detailsSx}>
              {/* Linha 1 */}
              <Box sx={rowSx}>
                {/* Tipo de Conta */}
                <Box sx={fieldSx}>
                  <FormControl fullWidth {...small}>
                    <InputLabel>{t("sharedAccountType")}</InputLabel>
                    <Select
                      label={t("sharedAccountType")}
                      value={item?.attributes?.form?.accountType || ""}
                      onChange={(e) =>
                        updateFormField("accountType", e.target.value)
                      }
                    >
                      <MenuItem value="individual">
                        {t("sharedAccountIndividual")}
                      </MenuItem>
                      <MenuItem value="company">
                        {t("sharedAccountCompany")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* CPF/CNPJ */}
                <Box sx={fieldSx}>
                  <TextField
                    {...small}
                    fullWidth
                    value={item?.attributes?.form?.taxId || ""}
                    onChange={(event) =>
                      setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: {
                            ...item.attributes.form,
                            taxId: event.target.value,
                          },
                        },
                      })
                    }
                    label={t("sharedCPFCNPJ")}
                  />
                </Box>

                {/* Telefone */}
                <Box sx={fieldSx}>
                  <TextField
                    {...small}
                    fullWidth
                    value={item.phone || ""}
                    onChange={(e) =>
                      setItem({ ...item, phone: e.target.value })
                    }
                    label={t("sharedPhone")}
                  />
                </Box>
              </Box>

              {/* Linha 2 */}
              <Box sx={rowSx}>
                {/* WhatsApp */}
                <Box sx={fieldSx}>
                  <TextField
                    {...small}
                    fullWidth
                    value={item?.attributes?.form?.whatsapp || ""}
                    onChange={(event) =>
                      setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: {
                            ...item.attributes.form,
                            whatsapp: event.target.value,
                          },
                        },
                      })
                    }
                    label={t("sharedWhatsapp")}
                  />
                </Box>

                {/* Telegram */}
                <Box sx={fieldSx}>
                  <TextField
                    {...small}
                    fullWidth
                    value={item?.attributes?.form?.telegramChatId || ""}
                    onChange={(event) =>
                      setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: {
                            ...item.attributes.form,
                            telegramChatId: event.target.value,
                          },
                        },
                      })
                    }
                    label={t("sharedTelegram")}
                  />
                </Box>

                {/* Mapa padrão */}
                <Box sx={fieldSx}>
                  <FormControl fullWidth {...small}>
                    <InputLabel>{t("mapDefault")}</InputLabel>
                    <Select
                      label={t("mapDefault")}
                      value={item.map || "openFreeMap"}
                      onChange={(e) =>
                        setItem({ ...item, map: e.target.value })
                      }
                    >
                      {mapStyles
                        .filter((style) => style.available)
                        .map((style) => (
                          <MenuItem key={style.id} value={style.id}>
                            <Typography component="span">
                              {style.title}
                            </Typography>
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Linha 3 */}
              <Box sx={rowSx}>
                {/* Formato de coordenadas */}
                <Box sx={fieldSx}>
                  <FormControl fullWidth {...small}>
                    <InputLabel>{t("settingsCoordinateFormat")}</InputLabel>
                    <Select
                      label={t("settingsCoordinateFormat")}
                      value={item.coordinateFormat || "dd"}
                      onChange={(e) =>
                        setItem({ ...item, coordinateFormat: e.target.value })
                      }
                    >
                      <MenuItem value="dd">
                        {t("sharedDecimalDegrees")}
                      </MenuItem>
                      <MenuItem value="ddm">
                        {t("sharedDegreesDecimalMinutes")}
                      </MenuItem>
                      <MenuItem value="dms">
                        {t("sharedDegreesMinutesSeconds")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Fuso horário */}
                <Box sx={fieldSx}>
                  <SelectField
                    fullWidth
                    {...small}
                    value={item.attributes?.timezone || ""}
                    onChange={(e) => updateAttributeValue("timezone", e.target.value)}
                    endpoint="/api/server/timezones"
                    keyGetter={(it) => it}
                    titleGetter={(it) => it}
                    label={t("sharedTimezone")}
                  />
                </Box>

                {/* Camada de POI */}
                <Box sx={fieldSx}>
                  <TextField
                    {...small}
                    fullWidth
                    value={item.poiLayer || ""}
                    onChange={(e) =>
                      setItem({ ...item, poiLayer: e.target.value })
                    }
                    label={t("mapPoiLayer")}
                  />
                </Box>
              </Box>

              {/* Linha 4 - Unidades (parte 1) */}
              <Box sx={rowSx}>
                <Box sx={fieldSx}>
                  <FormControl fullWidth {...small}>
                    <InputLabel>{t("settingsSpeedUnit")}</InputLabel>
                    <Select
                      label={t("settingsSpeedUnit")}
                      value={item.attributes?.speedUnit || "kn"}
                      onChange={(e) => updateAttributeValue("speedUnit", e.target.value)}
                    >
                      <MenuItem value="kn">{t("sharedKn")}</MenuItem>
                      <MenuItem value="kmh">{t("sharedKmh")}</MenuItem>
                      <MenuItem value="mph">{t("sharedMph")}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={fieldSx}>
                  <FormControl fullWidth {...small}>
                    <InputLabel>{t("settingsDistanceUnit")}</InputLabel>
                    <Select
                      label={t("settingsDistanceUnit")}
                      value={item.attributes?.distanceUnit || "km"}
                      onChange={(e) => updateAttributeValue("distanceUnit", e.target.value)}
                    >
                      <MenuItem value="km">{t("sharedKm")}</MenuItem>
                      <MenuItem value="mi">{t("sharedMi")}</MenuItem>
                      <MenuItem value="nmi">{t("sharedNmi")}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Linha 5 - Unidades (parte 2) */}
              <Box sx={rowSx}>
                <Box sx={fieldSx}>
                  <FormControl fullWidth {...small}>
                    <InputLabel>{t("settingsAltitudeUnit")}</InputLabel>
                    <Select
                      label={t("settingsAltitudeUnit")}
                      value={item.attributes?.altitudeUnit || "m"}
                      onChange={(e) => updateAttributeValue("altitudeUnit", e.target.value)}
                    >
                      <MenuItem value="m">{t("sharedMeters")}</MenuItem>
                      <MenuItem value="ft">{t("sharedFeet")}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={fieldSx}>
                  <FormControl fullWidth {...small}>
                    <InputLabel>{t("settingsVolumeUnit")}</InputLabel>
                    <Select
                      label={t("settingsVolumeUnit")}
                      value={item.attributes?.volumeUnit || "ltr"}
                      onChange={(e) => updateAttributeValue("volumeUnit", e.target.value)}
                    >
                      <MenuItem value="ltr">{t("sharedLiter")}</MenuItem>
                      <MenuItem value="usGal">{t("sharedUsGallon")}</MenuItem>
                      <MenuItem value="impGal">{t("sharedImpGallon")}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* -------------------------------------------------------------------
              ENDEREÇO
          ------------------------------------------------------------------- */}
          <Accordion disableGutters sx={{ mb: 2, borderRadius: 2, overflow: "hidden", border: `1px solid ${theme.palette.divider}` }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
              <Box display="flex" alignItems="center" gap={1.25}>
                <MapIcon sx={{ color: theme.palette.primary.main }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {t("sharedAddress")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details} sx={detailsSx}>
              <Box sx={rowSx}>
                {/* Endereço */}
                <Box sx={{ ...fieldSx, flex: 3 }}>
                  <TextField
                    {...small}
                    fullWidth
                    value={item?.attributes?.form?.address1 || ""}
                    onChange={(event) =>
                      setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: {
                            ...item.attributes.form,
                            address1: event.target.value,
                          },
                        },
                      })
                    }
                    label={t("sharedAddress")}
                  />
                </Box>

                {/* Bairro */}
                <Box sx={{ ...fieldSx, flex: 2 }}>
                  <TextField
                    {...small}
                    fullWidth
                    value={item?.attributes?.form?.address2 || ""}
                    onChange={(event) =>
                      setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: {
                            ...item.attributes.form,
                            address2: event.target.value,
                          },
                        },
                      })
                    }
                    label={t("sharedNeighborhood")}
                  />
                </Box>

                {/* CEP */}
                <Box sx={fieldSx}>
                  <TextField
                    {...small}
                    fullWidth
                    value={item?.attributes?.form?.postCode || ""}
                    onChange={(event) =>
                      setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: {
                            ...item.attributes.form,
                            postCode: event.target.value,
                          },
                        },
                      })
                    }
                    label={t("sharedPostalCode")}
                  />
                </Box>

                {/* Cidade */}
                <Box sx={fieldSx}>
                  <TextField
                    {...small}
                    fullWidth
                    value={item?.attributes?.form?.city || ""}
                    onChange={(event) =>
                      setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: {
                            ...item.attributes.form,
                            city: event.target.value,
                          },
                        },
                      })
                    }
                    label={t("sharedCity")}
                  />
                </Box>

                {/* Estado */}
                <Box sx={{ ...fieldSx, flex: 0.8 }}>
                  <TextField
                    {...small}
                    fullWidth
                    value={item?.attributes?.form?.state || ""}
                    onChange={(event) =>
                      setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: {
                            ...item.attributes.form,
                            state: event.target.value,
                          },
                        },
                      })
                    }
                    label={t("sharedState")}
                  />
                </Box>

                {/* País */}
                <Box sx={{ ...fieldSx, flex: 0.8 }}>
                  <TextField
                    {...small}
                    fullWidth
                    value={item?.attributes?.form?.country || ""}
                    onChange={(event) =>
                      setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: {
                            ...item.attributes.form,
                            country: event.target.value,
                          },
                        },
                      })
                    }
                    label={t("sharedCountry")}
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* -------------------------------------------------------------------
              LOCALIZAÇÃO / MAPA
          ------------------------------------------------------------------- */}
          <Accordion disableGutters sx={{ mb: 2, borderRadius: 2, overflow: "hidden", border: `1px solid ${theme.palette.divider}` }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
              <Box display="flex" alignItems="center" gap={1.25}>
                <LocationOnIcon sx={{ color: theme.palette.primary.main }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {t("sharedLocation")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details} sx={detailsSx}>
              <Box sx={rowSx}>
                <TextField
                  {...small}
                  type="number"
                  value={item.latitude || 0}
                  onChange={(e) =>
                    setItem({ ...item, latitude: Number(e.target.value) })
                  }
                  label={t("positionLatitude")}
                />
                <TextField
                  {...small}
                  type="number"
                  value={item.longitude || 0}
                  onChange={(e) =>
                    setItem({ ...item, longitude: Number(e.target.value) })
                  }
                  label={t("positionLongitude")}
                />
                <TextField
                  {...small}
                  type="number"
                  value={item.zoom || 0}
                  onChange={(e) =>
                    setItem({ ...item, zoom: Number(e.target.value) })
                  }
                  label={t("serverZoom")}
                />
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    const { lng, lat } = map.getCenter();
                    setItem({
                      ...item,
                      latitude: Number(lat.toFixed(6)),
                      longitude: Number(lng.toFixed(6)),
                      zoom: Number(map.getZoom().toFixed(1)),
                    });
                  }}
                >
                  {t("mapCurrentLocation")}
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* -------------------------------------------------------------------
              PERMISSÕES / SEGURANÇA
          ------------------------------------------------------------------- */}
          <Accordion disableGutters sx={{ mb: 2, borderRadius: 2, overflow: "hidden", border: `1px solid ${theme.palette.divider}` }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
              <Box display="flex" alignItems="center" gap={1.25}>
                <SecurityIcon sx={{ color: theme.palette.primary.main }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {t("sharedPermissions")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details} sx={detailsSx}>
              <Box sx={rowSx}>
                {/* Data de expiração com ícone de calendário */}
                <Box sx={fieldSx}>
                  <TextField
                    {...small}
                    label={t("userExpirationTime")}
                    value={
                      item.expirationTime
                        ? item.expirationTime.split("T")[0]
                        : "2099-01-01"
                    }
                    onChange={(e) => {
                      if (e.target.value) {
                        setItem({
                          ...item,
                          expirationTime: new Date(e.target.value).toISOString(),
                        });
                      }
                    }}
                    disabled={!manager}
                    InputProps={{
                      inputProps: { type: "date", lang: htmlLang },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            edge="end"
                            onClick={() => openDatePicker(expirationRef.current)}
                          >
                            <CalendarTodayIcon
                              sx={{ color: theme.palette.action.active }}
                            />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    inputRef={expirationRef}
                    className={classes.dateInput}
                    fullWidth
                  />
                </Box>

                <Box sx={fieldSx}>
                  <TextField
                    {...small}
                    fullWidth
                    type="number"
                    value={item.deviceLimit || 0}
                    onChange={(e) =>
                      setItem({ ...item, deviceLimit: Number(e.target.value) })
                    }
                    label={t("userDeviceLimit")}
                    disabled={!admin}
                  />
                </Box>
                <Box sx={fieldSx}>
                  <TextField
                    {...small}
                    fullWidth
                    type="number"
                    value={item.userLimit || 0}
                    onChange={(e) =>
                      setItem({ ...item, userLimit: Number(e.target.value) })
                    }
                    label={t("userUserLimit")}
                    disabled={!admin}
                  />
                </Box>
                <Box sx={{ ...fieldSx, flex: 0.8, display: "flex" }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setRevokeDialogOpen(true)}
                  >
                    {t("userRevokeToken")}
                  </Button>
                </Box>
              </Box>

              {/* Grupo de checkboxes em blocos compactos */}
              <FormGroup sx={{ gap: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={item.disabled}
                      onChange={(e) =>
                        setItem({ ...item, disabled: e.target.checked })
                      }
                      size="small"
                    />
                  }
                  label={t("sharedDisabled")}
                  disabled={!manager}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={item.administrator}
                      onChange={(e) =>
                        setItem({ ...item, administrator: e.target.checked })
                      }
                      size="small"
                    />
                  }
                  label={t("userAdmin")}
                  disabled={!admin}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={item.readonly}
                      onChange={(e) =>
                        setItem({ ...item, readonly: e.target.checked })
                      }
                      size="small"
                    />
                  }
                  label={t("serverReadonly")}
                  disabled={!manager}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={item.deviceReadonly}
                      onChange={(e) =>
                        setItem({ ...item, deviceReadonly: e.target.checked })
                      }
                      size="small"
                    />
                  }
                  label={t("userDeviceReadonly")}
                  disabled={!manager}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={item.limitCommands}
                      onChange={(e) =>
                        setItem({ ...item, limitCommands: e.target.checked })
                      }
                      size="small"
                    />
                  }
                  label={t("userLimitCommands")}
                  disabled={!manager}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={item.disableReports}
                      onChange={(e) =>
                        setItem({ ...item, disableReports: e.target.checked })
                      }
                      size="small"
                    />
                  }
                  label={t("userDisableReports")}
                  disabled={!manager}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={item.fixedEmail}
                      onChange={(e) =>
                        setItem({ ...item, fixedEmail: e.target.checked })
                      }
                      size="small"
                    />
                  }
                  label={t("userFixedEmail")}
                  disabled={!manager}
                />
              </FormGroup>
            </AccordionDetails>
          </Accordion>

          {/* -------------------------------------------------------------------
              ATRIBUTOS (mantém toda a lógica original do componente)
          ------------------------------------------------------------------- */}
          <EditAttributesAccordion
            attribute={attribute}
            attributes={filterAttributes(item.attributes)}
            setAttributes={updateAttributes}
            definitions={{ ...commonUserAttributes, ...userAttributes }}
            focusAttribute={attribute}
          />

          {/* -------------------------------------------------------------------
              EXCLUSÃO DA CONTA (fluxo original preservado)
          ------------------------------------------------------------------- */}
          {registrationEnabled && item.id === currentUser.id && !manager && (
            <Accordion disableGutters sx={{ mt: 2, borderRadius: 2, overflow: "hidden", border: `1px solid ${theme.palette.divider}` }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
                <Typography variant="subtitle1" color="error" sx={{ fontWeight: 600 }}>
                  {t("userDeleteAccount")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.details} sx={detailsSx}>
                <Box sx={rowSx}>
                  <TextField
                    {...small}
                    value={deleteEmail}
                    onChange={(e) => setDeleteEmail(e.target.value)}
                    label={t("userEmail")}
                    error={deleteFailed}
                    fullWidth
                  />
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDelete}
                    startIcon={<DeleteForeverIcon />}
                    sx={{ alignSelf: { xs: "stretch", sm: "center" } }}
                  >
                    {t("userDeleteAccount")}
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
        </>
      )}
      <Dialog
        open={revokeDialogOpen}
        onClose={closeRevokeDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogContent className={classes.details}>
          <TextField
            {...small}
            value={revokeToken}
            onChange={(e) => setRevokeToken(e.target.value)}
            label={t("userToken")}
            autoFocus
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRevokeDialog}>{t("sharedCancel")}</Button>
          <Button
            onClick={handleRevokeToken}
            disabled={!revokeToken}
            variant="contained"
          >
            {t("userRevokeToken")}
          </Button>
        </DialogActions>
      </Dialog>
    </EditItemView>
  );
};

export default UserPage;
